import {
  createContext,
  FC,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";

import {
  getConfigCatConfig,
  initConfigCat,
  refreshFlagState,
  FlagStateOptionalClient,
} from "../utils/configCatUtils";
import useLandingPageIds from "../hooks/useLandingPageIds";

const ConfigCatContext = createContext<FlagStateOptionalClient | null>(null);
export const useConfigCat = () => useContext(ConfigCatContext);

interface FeatureFlagProviderProps {
  flagState?: FlagStateOptionalClient;
  children: React.ReactNode;
}

const emptyFlagState = {};

export const FeatureFlagProvider: FC<FeatureFlagProviderProps> = ({
  children,
  flagState: initialFlagState = null,
}) => {
  const { sdkKey, ...configCatConfig } = getConfigCatConfig();
  const { masterId, variantId } = useLandingPageIds();
  // Skip refreshing flags on first render if we have flag values already
  const skipRefresh = useRef(!!initialFlagState?.flagValues);

  const [flagState, setFlagState] = useState<FlagStateOptionalClient | null>(
    () => {
      if (!sdkKey || initialFlagState?.configCatClient) {
        return initialFlagState;
      }

      return {
        ...initConfigCat({ ...configCatConfig, sdkKey }),
        ...initialFlagState,
      };
    },
  );

  useEffect(() => {
    if (flagState?.configCatClient && !skipRefresh.current) {
      refreshFlagState({
        client: flagState.configCatClient,
        landingPageId: masterId,
        variantId,
      }).then(setFlagState);
    }
    skipRefresh.current = false;
  }, [flagState?.configCatClient, masterId, variantId]);

  if (!sdkKey) {
    // Fallback mode for when the client does not get initialised, usually for tests.
    // Flags will instead resolve to an undefined value.
    return <>{children}</>;
  }

  if (!flagState?.flagValues) {
    // Wait for the feature flag client to initialise before rendering anything
    return null;
  }

  // Render children even when flags themselves are still loading.
  return (
    <ConfigCatContext.Provider value={flagState ?? emptyFlagState}>
      {children}
    </ConfigCatContext.Provider>
  );
};
